import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useLocale } from "@/context/LocaleContext";
import { AccountMenu } from "@/components/AccountMenu";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { useAuth } from "@/App";
import { HELP_SECTIONS } from "@/lib/helpPageCopy";
import { PROJECTS_LIST_PATH } from "@/lib/routes";
import { TAB_MAIN_TITLE_CLASS } from "@/lib/tab-page-title";

/** Tillåt bara interna sökvägar från location.state (undvik öppna omdirigeringar). */
function safeReturnPath(raw) {
  if (typeof raw !== "string") return PROJECTS_LIST_PATH;
  const p = raw.trim().split("#")[0].split("?")[0];
  if (!p.startsWith("/") || p.startsWith("//")) return PROJECTS_LIST_PATH;
  if (p.includes("..")) return PROJECTS_LIST_PATH;
  return p || PROJECTS_LIST_PATH;
}

export default function HelpPage() {
  const { user, logout } = useAuth();
  const { lang, t } = useLocale();
  const location = useLocation();
  const sections = HELP_SECTIONS[lang === "en" ? "en" : "sv"];

  const backTo = safeReturnPath(location.state?.returnTo);

  return (
    <div className="min-h-screen grain pb-16">
      <header className="border-b border-[var(--line)] px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sticky top-0 bg-[var(--bg)]/85 backdrop-blur z-30">
        <div className="flex items-center gap-2 min-w-0">
          <Link
            to={backTo}
            className="btn-ghost flex items-center gap-1 shrink-0"
            aria-label={t("project.back")}
          >
            <ChevronLeft size={16} aria-hidden />
            <span className="hidden sm:inline">{t("project.back")}</span>
          </Link>
          <h1 className={`${TAB_MAIN_TITLE_CLASS} truncate text-lg sm:text-2xl`}>{t("help.pageTitle")}</h1>
        </div>
        {user && user !== false ? (
          <AccountMenu
            user={user}
            helpReturnTo={backTo}
            onLogout={logout}
          />
        ) : (
          <LanguageSwitch matchedWidth />
        )}
      </header>

      <main className="mx-auto max-w-2xl px-4 sm:px-6 py-8 sm:py-10 space-y-10">
        {sections.map((sec, i) => (
          <section key={i} className="space-y-3">
            <h2 className="text-base sm:text-lg font-semibold text-[var(--accent)] tracking-wide">{sec.title}</h2>
            {sec.bullets && sec.bullets.length > 0 && (
              <ul className="list-disc pl-5 space-y-2 text-sm sm:text-[15px] text-[var(--text)]/95 leading-relaxed">
                {sec.bullets.map((b, k) => (
                  <li key={k}>{b}</li>
                ))}
              </ul>
            )}
            {(sec.paragraphs || []).map((p, j) => (
              <p key={j} className="text-sm sm:text-[15px] text-[var(--text)]/95 leading-relaxed">
                {p}
              </p>
            ))}
          </section>
        ))}
      </main>
    </div>
  );
}
